import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LanIcon from '@mui/icons-material/Lan';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { EMAIL } from 'src/logic/constants/links';
import { sm } from 'src/site-map';

export const pricingOptions = [
  {
    badge: 'BUYERS',
    title: 'Become a buyer',
    description:
      'Join a network of vetted sellers and streamline procurement - with 1% cashback on every confirmed order.',
    items: [
      'Just $50/month - cancel anytime',
      '1% cashback on all confirmed purchase orders',
      'First year FREE - launch with zero risk',
      'Priority onboarding and full support',
    ],
    link: sm.register.asBuyer.index.url,
  },
  {
    badge: 'SELLER',
    title: 'Become a seller',
    description: 'List your products, close faster. Pay only when you win. Sell to a real buyers. Zero upfront cost.',
    items: [
      'Zero registration fees',
      'Zero listing fees',
      'Commission-only model - pay when you sell',
      'Discoverable by verified, high-intent buyers',
    ],
    link: sm.register.asSeller.index.url,
  },
];

// =============================================================

export const intelligentProcurementItems = [
  {
    icon: LanIcon,
    title: 'Comprehensive Database for Seamless Automation',
    description:
      'eProcurement centralizes products, services, buyers, and sellers in a secure, integrated system. It automates procurement, reduces manual work, and speeds up sourcing—ensuring data integrity and streamlined workflows so your team can focus on high-value tasks.',
  },
  {
    icon: QuestionAnswerIcon,
    title: 'Enhanced Collaboration Platform',
    description:
      'eProcurement acts as a central hub for communication between buyers and sellers, allowing for efficient collaboration throughout the procurement cycle. With built-in approval workflows and real-time updates , the platform ensures seamless interaction.',
  },
  {
    icon: AutoFixHighIcon,
    title: 'AI-Driven Analytics Engine for Cost Savings',
    description: `eProcurement's powerful analytics engine goes beyond just tracking data — it identifies cost-saving opportunities by suggesting alternative products and services through intelligent AI recommendations by continuously analyzing usage patterns and market trends.`,
  },
];

// =============================================================

export const faqItems = [
  {
    title: 'What is eProcurement?',
    content:
      'eProcurement is an AI-powered, end-to-end sourcing and procurement platform that centralizes product and service catalogues, automates RFQs and POs, and provides real-time analytics to help you make smarter purchasing decisions.',
  },
  {
    title: 'How does eProcurement integrate with my existing systems?',
    content:
      'Our platform offers pre-built connectors and a robust API to seamlessly sync with ERPs, accounting software, and inventory management tools—so you can plug in without disrupting your workflows.',
  },
  {
    title: 'What features are included?',
    content: (
      <>
        Key features include:
        <ul style={{ paddingInline: '1em', marginBlock: 0 }}>
          <li>Intelligent product search & AI-driven recommendations</li>
          <li>Automated RFQ and PO generation with dynamic comparison tables</li>
          <li>Document management (invoices, delivery notes)</li>
          <li>Audit-ready reporting and dashboards</li>
        </ul>
      </>
    ),
  },
  {
    title: 'How long does implementation take?',
    content:
      'Implementation is instantaneous once you register and get approved as a user. For larger integrations with ERP systems, our onboarding team guides you through data migration, configuration, and user training to ensure a smooth, speedy rollout.',
  },
  {
    title: 'Is my procurement data secure?',
    content:
      'Absolutely. We use AES-256 encryption at rest, TLS 1.2+ in transit, role-based access controls, and regular third-party security audits to safeguard your information.',
  },
  {
    title: 'What pricing plans do you offer?',
    content: (
      <>
        We offer flexible, subscription-based pricing:
        <ul style={{ paddingInline: '1em', marginBlock: 0 }}>
          <li>Buyers: $50 monthly fee – 1% rebate on confirmed purchase orders.</li>
          <li>Sellers: Zero listing fees, and 2 - 5% commission fees on sold items. Only pay when you sell.</li>
        </ul>
      </>
    ),
  },
  {
    title: 'Can I manage multiple users and approval workflows?',
    content:
      'Yes. Define unlimited user roles, approval permissions to match your organization’s structure—ensuring compliance and accountability at every step.',
  },
  {
    title: 'How does the AI-driven recommendation engine work?',
    content:
      'Our AI continuously ingests and standardizes supplier catalogues and market benchmarks to suggest optimal products and suppliers—helping you save time and maximize cost savings.',
  },
  {
    title: 'What kind of customer support is available?',
    content:
      'We provide 24/5 live chat, email support, and a dedicated Customer Success Manager for all users. You’ll also have access to our online knowledge base and training webinars.',
  },
  {
    title: 'How do I get started with eProcurement?',
    content: (
      <>
        Simply register online to start using eProcurement instantaneously.
        <br />
        If you require additional guidance, book a free personalized demo on our website, or contact our sales team at{' '}
        <a href={`mailto:${EMAIL}`} className='text-blue-600 underline'>
          {EMAIL}
        </a>
        . We'll walk you through the platform, assess your needs, and outline next steps.
      </>
    ),
  },
];

// =============================================================

export type ProcurementOverviewItem = {
  badges: string[];
  title: string;
  description: string;
  image: string;
};
export const procurementOverviewItems: ProcurementOverviewItem[] = [
  {
    badges: ['Cloud Solution'],
    title: 'Cloud-Based Convenience',
    description:
      'Access your procurement solution anywhere, anytime. As a cloud-based platform, eProcurement offers flexibility, scalability, and zero downtime, enabling your team to work remotely or across multiple locations without the need for complex installations or infrastructure.',
    image: '/assets/images/procurement-overview/cloud-based-convenience.png',
  },
  {
    badges: ['AI-powered'],
    title: 'AI-Powered Automation & Real-Time Analytics',
    description:
      'Harness the power of AI to automate your procurement processes, from product selection to purchase order creation. Real-time analytics provide actionable insights into your spend, helping you make data-driven decisions with ease and optimize procurement workflows for maximum efficiency.',
    image: '/assets/images/procurement-overview/ai-automation-analytics.png',
  },
  {
    badges: ['Scale'],
    title: 'Supplier Management & Document Centre ',
    description:
      'Manage suppliers seamlessly with eProcurement’s robust supplier management tools. Store and organize all procurement documents in one secure, centralized location. Enjoy direct connectivity between buyers and sellers for efficient collaboration and streamlined transactions.',
    image: '/assets/images/procurement-overview/supplier-management-docs.png',
  },
  {
    badges: ['Integrations', 'Coming Soon'],
    title: 'ERP System Integration',
    description:
      'Easily integrate eProcurement with your existing ERP systems, including QuickBooks, SAP, Oracle, and Sage. Our seamless connectivity ensures a smooth flow of data across platforms, eliminating manual data entry, minimizing errors, and ensuring consistency across your entire tech ecosystem.',
    image: '/assets/images/procurement-overview/erp-integration.png',
  },
];
